import { useParams, Link, Navigate } from 'react-router-dom';
import SEO from '@/components/seo/SEO';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bot, Check, X, ArrowRight, Quote, ExternalLink } from 'lucide-react';
import { AGENTS } from '@/data/agents';

const SITE = 'https://getbitsy.ai';
const LAST_MODIFIED = '2026-04-25';

// Static color tokens — Tailwind needs literal classnames at build time.
const COLORS = {
  amber:   { ring: 'ring-amber-500/40',   bg: 'bg-amber-500/15',   text: 'text-amber-400',   border: 'border-amber-500/40',   hoverBg: 'hover:bg-amber-500/20' },
  cyan:    { ring: 'ring-cyan-500/40',    bg: 'bg-cyan-500/15',    text: 'text-cyan-400',    border: 'border-cyan-500/40',    hoverBg: 'hover:bg-cyan-500/20' },
  emerald: { ring: 'ring-emerald-500/40', bg: 'bg-emerald-500/15', text: 'text-emerald-400', border: 'border-emerald-500/40', hoverBg: 'hover:bg-emerald-500/20' },
  teal:    { ring: 'ring-teal-500/40',    bg: 'bg-teal-500/15',    text: 'text-teal-400',    border: 'border-teal-500/40',    hoverBg: 'hover:bg-teal-500/20' },
  orange:  { ring: 'ring-orange-500/40',  bg: 'bg-orange-500/15',  text: 'text-orange-400',  border: 'border-orange-500/40',  hoverBg: 'hover:bg-orange-500/20' },
};

export default function AgentDetailPage() {
  const { slug } = useParams();
  const agent = AGENTS[slug];

  if (!agent) return <Navigate to="/agents" replace />;

  const canonical = `${SITE}/agents/${agent.slug}`;
  const c = COLORS[agent.color] || COLORS.amber;

  // ─── JSON-LD Schemas ───────────────────────────────────────────
  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: `${agent.name} — ${agent.role}`,
    serviceType: 'Hotel AI Agent',
    description: agent.citable_answer,
    provider: {
      '@type': 'Organization',
      name: 'getbitsy.ai',
      url: SITE,
      sameAs: [`${SITE}/protocol`, `${SITE}/docs`],
    },
    areaServed: 'Worldwide',
    audience: { '@type': 'BusinessAudience', audienceType: 'Hotel Operators' },
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Included with getbitsy.ai listing — 5%+1% commission model',
    },
    url: canonical,
    isPartOf: { '@type': 'SoftwareApplication', name: 'getbitsy.ai', url: SITE },
    dateModified: LAST_MODIFIED,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: agent.faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Hotel AI Agents', item: `${SITE}/agents` },
      { '@type': 'ListItem', position: 3, name: agent.name, item: canonical },
    ],
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100" data-testid={`agent-page-${agent.slug}`}>
      <SEO
        title={`${agent.name} — ${agent.role} for Hotels`}
        description={agent.citable_answer}
        canonical={canonical}
        schema={[serviceSchema, faqSchema, breadcrumb]}
      />

      <header className="border-b border-zinc-800/60 px-6 py-4 flex justify-between items-center">
        <Link to="/agents" className="text-sm tracking-widest text-zinc-400 hover:text-zinc-200">
          ← Hotel AI Agents
        </Link>
        <Link to="/docs" className="text-xs tracking-widest text-zinc-500 hover:text-zinc-300">
          Documentation
        </Link>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16 space-y-16">
        {/* HERO */}
        <article>
          <Badge className={`${c.bg} ${c.text} ${c.border} mb-4`}>HOTEL AI AGENT</Badge>
          <div className="flex items-center gap-5 mb-6">
            <div className={`w-16 h-16 rounded-2xl ${c.bg} ring-4 ${c.ring} flex items-center justify-center`}>
              <Bot className={`w-8 h-8 ${c.text}`} />
            </div>
            <div>
              <h1 className="text-5xl font-semibold leading-none">{agent.name}</h1>
              <p className="text-zinc-400 mt-1">{agent.role}</p>
            </div>
          </div>
          <p className="text-xl text-zinc-300 leading-relaxed">{agent.tagline}</p>
        </article>

        {/* CITABLE ANSWER (40-100 word block — designed for LLM extraction) */}
        <section className={`relative pl-6 border-l-4 ${c.border}`} data-testid="citable-answer">
          <Quote className={`absolute -left-3 top-0 w-6 h-6 ${c.text} bg-[#0a0e1a]`} />
          <p className="text-lg text-zinc-100 leading-relaxed font-medium">{agent.citable_answer}</p>
        </section>

        {/* H2 — direct query match */}
        <section>
          <h2 className="text-3xl font-semibold mb-4">What does {agent.name} do?</h2>
          <p className="text-zinc-400 mb-6">
            {agent.name} is one of five autonomous AI agents that run hotels on the BHERMES Protocol —
            alongside <Inline>Bitsy</Inline>, <Inline>Marina</Inline>, <Inline>Sage</Inline>, <Inline>Argus</Inline>, and <Inline>Hermes</Inline>.
            The full set replaces traditional hotel SaaS with a middleman-free, on-chain rewarded stack.
          </p>
          <ul className="space-y-3">
            {agent.capabilities.map((c, i) => (
              <li key={i} className="flex gap-3 items-start">
                <Check className={`w-5 h-5 ${c.text} shrink-0 mt-0.5`} />
                <span className="text-zinc-200">{c}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* H2 — Comparison table (LLMs extract these almost perfectly) */}
        <section>
          <h2 className="text-3xl font-semibold mb-4">How does {agent.name} compare to traditional hotel software?</h2>
          <p className="text-zinc-400 mb-6">
            Direct head-to-head against the incumbent SaaS your operations team is probably evaluating.
          </p>
          <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="comparison-table">
                <thead>
                  <tr className="bg-zinc-950 border-b border-zinc-800">
                    <th className="text-left p-4 text-zinc-300 font-medium">Tool</th>
                    <th className="text-left p-4 text-zinc-300 font-medium">Approach</th>
                    <th className="text-left p-4 text-zinc-300 font-medium">vs {agent.name}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className={`border-b border-zinc-800 ${c.bg}`}>
                    <td className="p-4 font-semibold">{agent.name} <span className={`${c.text} text-xs`}>(getbitsy.ai)</span></td>
                    <td className="p-4 text-zinc-300">{agent.tagline}</td>
                    <td className={`p-4 ${c.text} font-medium`}>Reference</td>
                  </tr>
                  {agent.competitors.map((c, i) => (
                    <tr key={i} className="border-b border-zinc-800/60 last:border-0">
                      <td className="p-4 font-medium text-zinc-200">{c.name}</td>
                      <td className="p-4 text-zinc-400">Closed-platform incumbent</td>
                      <td className="p-4 text-zinc-500 flex items-start gap-2">
                        <X className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" /> {c.flaw}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </section>

        {/* H2 — FAQ with question-shaped headers (highest GEO leverage) */}
        <section>
          <h2 className="text-3xl font-semibold mb-6">Frequently asked about {agent.name}</h2>
          <div className="space-y-4">
            {agent.faq.map((f, i) => (
              <article key={i} className="bg-zinc-900 border border-zinc-800 rounded-lg p-5" data-testid={`faq-${i}`}>
                <h3 className="text-lg font-medium text-zinc-100 mb-2">{f.q}</h3>
                <p className="text-zinc-400 leading-relaxed">{f.a}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Cross-links to other agents (internal LLM citation graph) */}
        <section>
          <h2 className="text-3xl font-semibold mb-6">Other agents in the BHERMES Protocol</h2>
          <div className="grid md:grid-cols-2 gap-3">
            {Object.values(AGENTS).filter((a) => a.slug !== agent.slug).map((a) => (
              <Link key={a.slug} to={`/agents/${a.slug}`}
                className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4 hover:border-zinc-700 hover:bg-zinc-900 transition flex items-center justify-between gap-3"
                data-testid={`cross-link-${a.slug}`}>
                <div>
                  <p className="font-medium text-zinc-100">{a.name}</p>
                  <p className="text-xs text-zinc-500">{a.role}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-zinc-500" />
              </Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="grid md:grid-cols-2 gap-4 pt-4">
          <Link to="/why-getbitsy" className={`rounded-xl ${c.border} border ${c.bg} p-6 ${c.hoverBg} transition`}>
            <p className={`${c.text} text-xs tracking-widest mb-1`}>FOR HOTELIERS</p>
            <p className="text-lg font-semibold">See ROI vs Booking.com</p>
            <p className="text-zinc-400 text-sm mt-1">Interactive calculator and competitor table.</p>
          </Link>
          <Link to="/docs" className="rounded-xl border border-zinc-700 bg-zinc-900/50 p-6 hover:bg-zinc-900 transition">
            <p className="text-zinc-400 text-xs tracking-widest mb-1">FOR DEVELOPERS</p>
            <p className="text-lg font-semibold">Technical documentation</p>
            <p className="text-zinc-400 text-sm mt-1">Architecture, API endpoints, schema.</p>
          </Link>
        </section>
      </main>

      <footer className="border-t border-zinc-800/60 text-center py-6 text-[11px] text-zinc-600 tracking-wider">
        Powered by BHERMES · M.O.A.T. · <Link to="/" className="hover:text-zinc-400">getbitsy.ai</Link>
        <span className="mx-2">·</span>
        <Link to="/llms.txt" className="hover:text-zinc-400 inline-flex items-center gap-1">
          llms.txt <ExternalLink className="w-3 h-3" />
        </Link>
      </footer>
    </div>
  );
}

function Inline({ children }) {
  const slug = String(children).toLowerCase();
  const exists = AGENTS[slug];
  if (!exists) return <span>{children}</span>;
  return <Link to={`/agents/${slug}`} className="text-amber-400 hover:underline">{children}</Link>;
}
